import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  type BaseQueryApi,
} from '@reduxjs/toolkit/query';
import { config } from '../../config/environment';
import { actions } from '../constants/appActions';
import {
  clearAccessExpiry,
  isAuthBootstrapRequest,
  isRefreshRequest,
  isSafeRetryMethod,
  needsProactiveRefresh,
  recordAccessExpiry,
  requestMethod,
  TOKEN_REFRESH_PATH,
  withSingleFlightRefresh,
} from './tokenSession';

export const BASE_URL = config.baseApiUrl;

type PrepareHeadersWithTokenArgs = {
  headers: Headers;
  api: Pick<
    BaseQueryApi,
    'endpoint' | 'getState' | 'extra' | 'type' | 'forced'
  >;
};

export const rawBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: 'include',
  prepareHeaders: prepareHeadersWithToken,
});

export async function prepareHeadersWithToken(
  headers: Headers,
  api: PrepareHeadersWithTokenArgs['api'],
): Promise<Headers> {
  const state = api.getState() as { auth?: { current: { userid?: number } } };
  if (headers.get('X-FormData') !== 'true') {
    headers.set('Content-Type', 'application/json');
  } else {
    headers.delete('Content-Type');
    headers.delete('X-FormData');
  }

  return headers;
}

type AuthState = {
  auth?: { current?: { userid?: number | null } };
};

const signOutOnInvalidSession = async (api: BaseQueryApi) => {
  clearAccessExpiry();
  const userid = (api.getState() as AuthState).auth?.current?.userid;
  if (userid) {
    const { actionExecution } = await import('./appHandling');
    await actionExecution({
      action: actions.logoutCurrent,
      dispatch: api.dispatch,
      args: { userid },
    });
  }
};

const readExpiresIn = (data: unknown): number | null => {
  if (!data || typeof data !== 'object') return null;
  const value = (data as { access_expires_in?: unknown }).access_expires_in;
  return typeof value === 'number' ? value : null;
};

const refreshAccessToken = async (
  api: BaseQueryApi,
  extra: unknown,
): Promise<boolean> =>
  withSingleFlightRefresh(async () => {
    const refreshResult = await rawBaseQuery(
      {
        url: TOKEN_REFRESH_PATH,
        method: 'POST',
        credentials: 'include',
      },
      api,
      extra as object,
    );
    if (refreshResult.error) {
      await signOutOnInvalidSession(api);
      return false;
    }
    const expiresIn = readExpiresIn(refreshResult.data);
    if (expiresIn) recordAccessExpiry(expiresIn);
    else recordAccessExpiry(300);
    return true;
  });

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extra) => {
  if (!isAuthBootstrapRequest(args) && needsProactiveRefresh()) {
    const refreshed = await refreshAccessToken(api, extra);
    if (!refreshed) {
      return {
        error: {
          status: 401,
          data: { detail: 'Authentication expired' },
        },
      };
    }
  }

  let result = await rawBaseQuery(args, api, extra);

  if (result?.error?.status === 401 && !isRefreshRequest(args)) {
    const refreshed = await refreshAccessToken(api, extra);
    if (!refreshed) {
      return result;
    }
    if (isSafeRetryMethod(requestMethod(args))) {
      result = await rawBaseQuery(args, api, extra);
    }
  }

  return result;
};
